import React, { useState } from "react";
import { Lock, X } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";

const PLAN_HIERARCHY = { starter: 0, pro: 1, growth: 2 };

export default function LockedFeature({ children, requiredPlan = "pro", currentPlan = "starter" }) {
  const [showModal, setShowModal] = useState(false);
  const navigate = useNavigate();
  // 🔴 COL.11 -- "Ver planes" lleva a /app/onboarding, que exige `configuracion`, la misma
  //    capacidad con la que el menú decide si enseña esa entrada, y leída del mismo sitio.
  //    Sin esto el modal ofrece una puerta que el servidor cierra con 403 (DT.71).
  //    ⚠️ Esconder no es un permiso: el backend ya rechaza en tres capas (COL.9).
  const { puede } = useAuth();

  const currentLevel = PLAN_HIERARCHY[currentPlan] ?? 0;
  const requiredLevel = PLAN_HIERARCHY[requiredPlan] ?? 1;

  if (currentLevel >= requiredLevel) return children;

  const planLabel = requiredPlan.charAt(0).toUpperCase() + requiredPlan.slice(1);

  return (
    <>
      <div className="relative rounded-2xl overflow-hidden">
        <div className="pointer-events-none select-none opacity-25 blur-[1px]">{children}</div>
        <div
          className="absolute inset-0 flex flex-col items-center justify-center cursor-pointer"
          onClick={() => setShowModal(true)}
        >
          <div className="bg-white/90 backdrop-blur-sm rounded-2xl shadow-lg px-6 py-4 text-center">
            <Lock className="w-6 h-6 text-slate-500 mx-auto mb-1.5" />
            <p className="text-sm font-semibold text-slate-800">Disponible en plan {planLabel}</p>
            <p className="text-xs text-slate-400 mt-0.5">Haz clic para ver planes</p>
          </div>
        </div>
      </div>

      {showModal && (
        <div className="fixed inset-0 bg-slate-900/40 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100">
              <span className="font-display font-bold">Función bloqueada</span>
              <button onClick={() => setShowModal(false)} className="p-1.5 text-slate-400 hover:text-slate-700 rounded-lg hover:bg-slate-100">
                <X className="w-4 h-4" />
              </button>
            </div>
            <div className="p-5 space-y-3">
              <div className="flex items-center gap-3 p-3 bg-blue-50 rounded-xl">
                <Lock className="w-5 h-5 text-brand-electric shrink-0" />
                <div>
                  <p className="text-sm font-semibold text-slate-800">Requiere plan {planLabel}</p>
                  <p className="text-xs text-slate-500">Activa tu plan para desbloquear esta función</p>
                </div>
              </div>
              <p className="text-sm text-slate-600">
                Accede a automatización avanzada, campañas de recuperación, análisis detallado y más.
              </p>
            </div>
            <div className="px-5 py-4 border-t border-slate-100 flex gap-2 justify-end">
              <button
                onClick={() => setShowModal(false)}
                className="text-sm px-4 py-2 rounded-lg bg-slate-100 hover:bg-slate-200 text-slate-700 font-medium"
              >
                Cerrar
              </button>
              {puede("configuracion") && (
              <button
                onClick={() => { setShowModal(false); navigate("/app/onboarding"); }}
                className="text-sm px-4 py-2 rounded-lg bg-brand-electric text-white hover:bg-blue-700 font-medium"
              >
                Ver planes →
              </button>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
}
